/**
 * @module  public/js/core/apiclient.js
 * @class   ApiClient
 * @summary HTTP gateway that wraps fetch with auth, JSON helpers and error banners.
 *
 * @description
 * ApiClient injects Firebase ID tokens, serialises bodies and parses JSON to simplify HTTP calls.
 * It raises banner errors via ErrorDisplayService and offers helper URL builders for CRM flows.
 *
 * ## Key Features
 * – Adds Firebase ID token automatically when `auth: true`.
 * – Auto-stringifies request bodies and parses JSON responses.
 * – Centralised error handling through ErrorDisplayService banners.
 * – Exposes URL builder helpers for CRM connection and store methods.
 *
 * ## Public API
 * – `request(url, opts)` — low-level fetch with optional auth and error handling.
 * – `fetchJson(url, opts)` — high-level JSON helper with auto body/stringify.
 * – `getConnectedCRM({ userId })` — fetch current CRM provider status.
 * – `generateInstallUrl({ userId, provider })` — build OAuth install URL.
 * – `disconnectCRM({ userId })` — POST to disconnect CRM.
 * – `getAccountsByOwner(params)` — build accounts-query URL.
 * – `getPipelines(params)` — build pipelines-query URL.
 * – `updateAccountDetails(params)` — build account-update URL.
 *
 * ## Dependencies
 * { AuthService, EndpointService, ErrorDisplayService }
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

import endpointsDefault from '../core/endpoints-service.js';
import useTenant from '../core/useTenant.js';

const defaultTenantStore = useTenant();

export default class ApiClient {
  /**
   * @param {Object} opts
   * @param {import("../services/authservice.js").default}         opts.authService
   * @param {import("../services/errordisplayservice.js").default} [opts.errorDisplayService]
   * @param {import("./endpoints-service.js").EndpointService}     [opts.endpointService=endpointsDefault]
   * @param {ReturnType<import("../core/useTenant.js").default>}   [opts.tenantStore]
   * @param {(forceRefresh?: boolean) => Promise<string|null>}     [opts.getAppCheckToken]
   */
  constructor({
    authService,
    errorDisplayService,
    endpointService = endpointsDefault,
    tenantStore = defaultTenantStore,
    getAppCheckToken = null,
  }) {
    if (!authService || !endpointService) {
      throw new Error('ApiClient: missing dependencies');
    }
    this.auth = authService;
    this.ui = errorDisplayService;
    this.endpoints = endpointService;
    this.tenantStore = tenantStore;
    this.getAppCheckToken =
      typeof getAppCheckToken === 'function'
        ? getAppCheckToken
        : typeof window !== 'undefined' &&
            typeof window.__tsGetAppCheckToken === 'function'
          ? window.__tsGetAppCheckToken
          : null;
  }

  /* ────────────────────────────────────────────────
   * 1. Low-level helper – use sparingly
   * ──────────────────────────────────────────────── */

  /**
   * @param {string} url
   * @param {RequestInit & { auth?: boolean, rawResponse?: boolean }} [opts]
   * @returns {Promise<any>}
   */
  async request(url, opts = {}) {
    const { auth = false, rawResponse = false, ...fetchOpts } = opts;
    const baseHeaders = new Headers(fetchOpts.headers);
    const authSvc = auth ? this.auth || window?.di?.auth : null;
    if (auth && !authSvc) {
      throw new Error('ApiClient: auth service not configured');
    }

    /** @type {Response} */
    let res;
    let attempt = 0;
    do {
      const headers = new Headers(baseHeaders);
      if (auth && authSvc) {
        const user = await authSvc.requireAuth();
        const token = await user.getIdToken(attempt === 1);
        headers.set('Authorization', `Bearer ${token}`);

        try {
          const origin =
            typeof window !== 'undefined' && window?.location?.origin
              ? window.location.origin
              : 'https://example.com';
          const target = new URL(url, origin);
          const headerTenant = headers.get('x-tenant-id');
          const tenantFromStore = this.tenantStore?.getTenantId?.();
          let tenant = headerTenant;
          if (!tenant && tenantFromStore) {
            let isAdmin = false;
            try {
              const { claims } = await user.getIdTokenResult();
              isAdmin =
                claims?.roles?.admin === true ||
                claims?.role === 'admin' ||
                claims?.role === 'super_admin';
            } catch (claimError) {
              console.warn('[ApiClient] failed to read role claims', {
                message: claimError?.message || String(claimError),
              });
            }
            if (isAdmin) {
              tenant = tenantFromStore;
            }
          }
          if (!tenant) {
            tenant = user.tenantId || null;
          }
          if (
            tenant &&
            (target.hostname.endsWith('cloudfunctions.net') ||
              target.hostname.endsWith('firebaseapp.com'))
          ) {
            headers.set('x-tenant-id', tenant);
          }
        } catch (error) {
          console.warn('[ApiClient] failed to inspect request URL', {
            message: error?.message || String(error),
          });
        }
      }

      if (typeof this.getAppCheckToken === 'function') {
        try {
          const appCheckToken = await this.getAppCheckToken(attempt === 1);
          if (appCheckToken) {
            headers.set('X-Firebase-AppCheck', appCheckToken);
          }
        } catch (error) {
          console.warn('[ApiClient] failed to resolve App Check token', {
            message: error?.message || String(error),
          });
        }
      }

      const requestInit = {
        credentials: 'same-origin',
        ...fetchOpts,
        headers,
      };

      res = await fetch(url, requestInit);
      const shouldRetry =
        auth && attempt === 0 && (res.status === 401 || res.status === 403);
      if (!shouldRetry) {
        break;
      }
      attempt += 1;
    } while (attempt < 2);

    if (rawResponse) {
      return res;
    }

    if (!res.ok) {
      let detail = '';
      try {
        const ct = res.headers.get('content-type') || '';
        const body = await (ct.includes('json') ? res.json() : res.text());
        if (ct.includes('json') && body && typeof body === 'object') {
          detail = body.error || '';
        } else if (typeof body === 'string') {
          detail = body;
        }
      } catch {
        /* ignore body parsing errors */
      }

      const msg = `HTTP ${res.status} — ${detail ? `${detail} — ${url}` : url}`;
      this.ui?.showBanner?.(msg, 'error', 3000);
      throw new Error(msg);
    }

    const isJson = res.headers.get('content-type')?.includes('json');
    return isJson ? res.json() : res.text();
  }

  /* ────────────────────────────────────────────────
   * 2. High-level helper – preferred entry-point
   * ──────────────────────────────────────────────── */

  /**
   * @param {string} url
   * @param {object} [opts]
   * @param {boolean} [opts.auth]  – add Bearer token automatically
   * @param {object}  [opts.body]  – serialised as JSON
   * @returns {Promise<any>}
   */
  async fetchJson(url, opts = {}) {
    const { body, ...rest } = opts;
    const final = { ...rest, headers: { ...(rest.headers || {}) } };
    let finalBody = body;
    if (
      body &&
      typeof body === 'object' &&
      !Array.isArray(body) &&
      !Object.prototype.hasOwnProperty.call(body, 'tenantId')
    ) {
      const tenantId = this.tenantStore?.getTenantId?.();
      if (tenantId) {
        finalBody = { tenantId, ...body };
      }
    }

    /* auto-stringify request body */
    if (finalBody !== undefined) {
      final.body = JSON.stringify(finalBody);
      final.headers['Content-Type'] = 'application/json';
      if (!final.method) final.method = 'POST';
    }

    return this.request(url, final);
  }

  /* ────────────────────────────────────────────────
   * 3. CRM-connection helpers (used by CRMConnectionService)
   * ──────────────────────────────────────────────── */

  /** GET …/crm/status?userId=… → { provider } */
  getConnectedCRM({ userId }) {
    return this.request(
      this.endpoints.buildUrl(this.endpoints.roots.crmStatus, { userId }),
      { auth: true }
    );
  }

  /** GET generateInstallUrl?userId&provider=… → { installUrl } */
  generateInstallUrl({ userId, provider }) {
    return this.request(
      this.endpoints.generateInstallUrl({ userId, provider }),
      { auth: true }
    );
  }

  /** POST …/crm/disconnect */
  disconnectCRM({ userId }) {
    return this.fetchJson(this.endpoints.roots.crmDisconnect, {
      auth: true,
      body: { userId },
    });
  }

  /* ────────────────────────────────────────────────
   * 4. Pass-through helpers so Stores stay terse
   * ──────────────────────────────────────────────── */

  /* These simply build *URLs*.  The calling Store passes the URL back into
     fetchJson()/request() so that auth-token & error handling remain central. */

  getAccountsByOwner(params) {
    return this.endpoints.getAccountsByOwner(params);
  }
  getPipelines(params) {
    return this.endpoints.getPipelines(params);
  }
  updateAccountDetails(params) {
    return this.endpoints.updateAccountDetails(params);
  }

  /* Add other one-liners as your Stores need them … */
}
